import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWebRTC } from "@/hooks/useWebRTC";
import { useRecording } from "@/hooks/useRecording";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVirtualBackground } from "@/hooks/useVirtualBackground";
import { useSubscription } from "@/hooks/useSubscription";
import { useConnectivity } from "@/contexts/ConnectivityContext";
import { PreJoinScreen } from "@/components/PreJoinScreen";
import { AudioCheckPrompt } from "@/components/AudioCheckPrompt";
import WaitingRoom from "@/components/WaitingRoom";
import WaitingRoomQueue from "@/components/WaitingRoomQueue";
import { formatMeetingCode, getMeetingLink } from "@/lib/meetingUtils";
import VirtualBackgroundSelector from "@/components/VirtualBackgroundSelector";
import FloatingReactions from "@/components/FloatingReactions";
import ReactionPicker from "@/components/ReactionPicker";
import type { ShortcutDefinition } from "@/components/KeyboardShortcutsHelp";
import { RecordingConsent } from "@/components/RecordingConsent";
import { useShortcutsModal } from "@/contexts/ShortcutsContext";
import { z } from "zod";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { AnalyticsUserContext, trackShortcutUsed } from "@/lib/monitoring/analytics";

const chatMessageSchema = z.string().trim().min(1).max(1000, "Message must be less than 1000 characters");
const guestNameSchema = z.string().trim().min(1).max(50, "Name must be less than 50 characters");

type MeetingState = 'idle' | 'prejoin' | 'connecting' | 'live' | 'reconnecting' | 'ended';

type DevicePrefState = {
  micId?: string;
  speakerId?: string;
  cameraId?: string;
};

const DEVICE_PREF_KEY = 'meethub_device_prefs';

// Generate or retrieve a unique guest UUID for the session
const getGuestUserId = (): string => {
  const storageKey = 'guestUserId';
  let guestId = sessionStorage.getItem(storageKey);
  
  if (!guestId) {
    // Generate a unique UUID for this guest session
    guestId = crypto.randomUUID();
    sessionStorage.setItem(storageKey, guestId);
  }
  
  return guestId;
};

import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  Phone,
  Users,
  MessageCircle,
  Send,
  Copy,
  Settings,
  ArrowLeft,
  PhoneOff,
  Circle,
  Check,
  SwitchCamera,
  PictureInPicture,
  Grid3x3,
  Maximize2,
  Waves,
  FlipHorizontal,
  Hand,
  Megaphone,
  Timer,
  AlarmClock,
  LineChart,
  Bolt,
  RefreshCcw,
  AlertCircle,
  Pin,
  PinOff,
  X
} from "lucide-react";
import { format } from "date-fns";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { ParticipantVideoGrid, type GridParticipantTile } from "@/components/ParticipantVideoGrid";
import { MobileChatDrawer } from "@/components/MobileChatDrawer";
import { MobileParticipantsDrawer } from "@/components/MobileParticipantsDrawer";
import { MeetingChatSidebar } from "@/components/MeetingChatSidebar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MeetingQRCode } from "@/components/MeetingQRCode";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  host_id: string;
  is_active: boolean;
  is_recording: boolean;
  waiting_room_enabled: boolean;
  is_webinar_mode: boolean;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
}

interface Participant {
  id: string;
  user_id: string;
  role: string;
  is_video_enabled: boolean;
  is_audio_enabled: boolean;
  is_screen_sharing: boolean;
  guest_name?: string | null;
  avatar_url?: string | null;
  profiles: {
    display_name: string;
    avatar_url: string | null;
  } | null;
}

// ✅ Missing type used throughout chat logic
type MessageReactions = Record<string, string[]>;

type ChatAttachment = {
  url: string;
  name: string;
  size?: number;
  type?: string;
  caption?: string;
} | null;

interface MeetingChatMessage {
  id: string;
  meeting_id?: string;
  user_id: string;
  guest_name?: string | null;
  message: string;
  message_type: "text" | "file" | "reaction";
  created_at: string;

  profiles?: {
    display_name?: string | null;
    avatar_url?: string | null;
  } | null;

  reactions?: MessageReactions;
  attachment?: ChatAttachment;

  // UI-only flags
  isPending?: boolean;
}


const ICEBREAKER_QUESTIONS = [
  {
    id: "confidence",
    prompt: "How confident are we about today's topic?",
    options: ["Need clarity", "Somewhat ready", "Fully confident"],
  },
  {
    id: "energy",
    prompt: "Energy check-in for this session",
    options: ["Chill", "Focused", "Energized"],
  },
  {
    id: "focus",
    prompt: "What's the most important outcome today?",
    options: ["Decisions", "Ideas", "Status clarity"],
  },
];

const PENDING_CHAT_TYPE = "chat-message";

const getStoredDevicePrefs = (): DevicePrefState => {
  if (typeof window === 'undefined') return {};

  try {
    const raw = localStorage.getItem(DEVICE_PREF_KEY);
    if (raw) return JSON.parse(raw) as DevicePrefState;
  } catch (error) {
    console.warn('Failed to parse stored device prefs', error);
  }

  return {
    micId: localStorage.getItem('preferredMicId') || undefined,
    speakerId: localStorage.getItem('preferredSpeakerId') || undefined,
    cameraId: localStorage.getItem('preferredCameraId') || undefined,
  };
};


const meetingShortcuts: ShortcutDefinition[] = [
  {
    keys: ["M"],
    description: "Toggle microphone (mute/unmute)",
    category: "Audio & Video",
  },
  {
    keys: ["V"],
    description: "Toggle camera (on/off)",
    category: "Audio & Video",
  },
  {
    keys: ["N"],
    description: "Toggle noise suppression",
    category: "Audio & Video",
  },
  {
    keys: ["G"],
    description: "Switch between gallery and default view",
    category: "View",
  },
  {
    keys: ["B"],
    description: "Open virtual background selector",
    category: "Effects",
  },
  {
    keys: ["?"],
    description: "Show/hide this help panel",
    category: "Help",
  },
];

export default function MeetingRoom() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerShortcuts } = useShortcutsModal();
  const { isOnline, pendingEdits, savePendingEdit, consumePendingEdits } = useConnectivity();
  const isMobile = useIsMobile();
  const { flags } = useFeatureFlags();
  const { canUseRecording } = useSubscription();
  const userContext: AnalyticsUserContext = useMemo(
    () => ({ id: user?.id, email: user?.email }),
    [user?.email, user?.id]
  );
  const initialDevicePrefs = useMemo(getStoredDevicePrefs, []);
  const [hostName, setHostName] = useState<string>("Host");
  const [meetingState, setMeetingState] = useState<MeetingState>('idle');
  const [resolvingMeetingLink, setResolvingMeetingLink] = useState(false);
  
  // Get guest name from URL params if provided
  useEffect(() => {
    const guestNameFromUrl = searchParams.get('guestName');
    if (guestNameFromUrl && !user) {
      const validation = guestNameSchema.safeParse(guestNameFromUrl);
      if (validation.success) {
        sessionStorage.setItem('guestName', validation.data);
      }
    }
  }, [searchParams, user]);

  useEffect(() => {
    registerShortcuts(meetingShortcuts);
  }, [registerShortcuts]);
  
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [messages, setMessages] = useState<MeetingChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreJoin, setShowPreJoin] = useState(true);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [isAdmitted, setIsAdmitted] = useState(false);
  const [participantInfoMap, setParticipantInfoMap] = useState<Map<string, { name: string; isVideoEnabled: boolean; isAudioEnabled: boolean }>>(new Map());
  const [isWebinarMode, setIsWebinarMode] = useState(false);
  const [raisedHands, setRaisedHands] = useState<Array<{ userId: string; name: string; timestamp: number; invited?: boolean }>>([]);
  const [hasRaisedHand, setHasRaisedHand] = useState(false);
  const [speakingDurations, setSpeakingDurations] = useState<Map<string, { totalMs: number; lastStarted?: number }>>(new Map());
  const [speakingLimitMinutes, setSpeakingLimitMinutes] = useState<number | "">("");
  const [meetingStartTime] = useState<number>(() => Date.now());
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [plannedDurationMinutes, setPlannedDurationMinutes] = useState(45);
  const [scheduledStartTime, setScheduledStartTime] = useState<Date | null>(null);
  const [isBeforeScheduledStart, setIsBeforeScheduledStart] = useState(false);
  const [scheduledCountdown, setScheduledCountdown] = useState("");
  const [icebreakerIndex, setIcebreakerIndex] = useState(0);
  const [icebreakerResponses, setIcebreakerResponses] = useState<Record<string, Record<string, number>>>({});
  const [answeredIcebreakers, setAnsweredIcebreakers] = useState<Set<string>>(new Set());

  // Media states
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [isPipEnabled, setIsPipEnabled] = useState(false);
  const [pipSupported, setPipSupported] = useState(false);
  const [viewMode, setViewMode] = useState<"default" | "gallery" | "spotlight">("default");
  const [activeSpeaker, setActiveSpeaker] = useState<string | null>(null);
  const [spotlightedParticipant, setSpotlightedParticipant] = useState<string | null>(null);
  const [virtualBackground, setVirtualBackground] = useState<'none' | 'blur' | string>('none');
  const [backgroundSelectorOpen, setBackgroundSelectorOpen] = useState(false);
  const [reactions, setReactions] = useState<Array<{ id: string; emoji: string; x: number; timestamp: number }>>([]);
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [isMirrored, setIsMirrored] = useState(true);
  const [joinedAtTime, setJoinedAtTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>("00:00:00");
  const [videoQuality, setVideoQuality] = useState<'low' | 'medium' | 'high'>(() => {
    const saved = localStorage.getItem('videoQuality');
    return (saved as 'low' | 'medium' | 'high') || 'medium';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [participantsPanelOpen, setParticipantsPanelOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [preferredMicDeviceId, setPreferredMicDeviceId] = useState<string | undefined>(() => initialDevicePrefs.micId);
  const [preferredSpeakerDeviceId, setPreferredSpeakerDeviceId] = useState<string | undefined>(() => initialDevicePrefs.speakerId);
  const [preferredCameraDeviceId, setPreferredCameraDeviceId] = useState<string | undefined>(() => initialDevicePrefs.cameraId);
  const [showAudioCheck, setShowAudioCheck] = useState(false);
  const [audioCheckCompleted, setAudioCheckCompleted] = useState(false);
  const [joiningOverlayVisible, setJoiningOverlayVisible] = useState(false);
  const [statusBarExpanded, setStatusBarExpanded] = useState(true);
  const [meetingInitialized, setMeetingInitialized] = useState(false);
  const chatTimestampsRef = useRef<number[]>([]);
  const statusBarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const joiningOverlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTo = useCallback((next: MeetingState) => {
    const allowedTransitions: Record<MeetingState, MeetingState[]> = {
      idle: ['prejoin'],
      prejoin: ['connecting', 'ended'],
      connecting: ['live', 'reconnecting', 'ended'],
      live: ['reconnecting', 'ended'],
      reconnecting: ['live', 'ended'],
      ended: [],
    };

    setMeetingState((current) => (allowedTransitions[current]?.includes(next) ? next : current));
  }, []);

  useEffect(() => {
    if (showPreJoin) {
      transitionTo('prejoin');
    }
  }, [showPreJoin, transitionTo]);
  const meetingIdIsValid = useMemo(
    () => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(meetingId || ""),
    [meetingId]
  );
  const resolvedMeetingId = useMemo(
    () => meeting?.id || (meetingIdIsValid ? meetingId : undefined),
    [meeting?.id, meetingId, meetingIdIsValid]
  );
  const joinLinkValue = useMemo(
    () => meeting?.meeting_code ? getMeetingLink(meeting.meeting_code) : "",
    [meeting?.meeting_code]
  );
  const canControlLiveMedia = meetingState === 'live' || meetingState === 'connecting' || meetingState === 'reconnecting';
  const persistMeetingDevicePrefs = useCallback((prefs: DevicePrefState) => {
    const nextPrefs: DevicePrefState = {
      micId: prefs.micId ?? preferredMicDeviceId,
      speakerId: prefs.speakerId ?? preferredSpeakerDeviceId,
      cameraId: prefs.cameraId ?? preferredCameraDeviceId,
    };

    try {
      localStorage.setItem(DEVICE_PREF_KEY, JSON.stringify(nextPrefs));
    } catch (error) {
      console.warn('Unable to persist meeting device preferences', error);
    }
  }, [preferredCameraDeviceId, preferredMicDeviceId, preferredSpeakerDeviceId]);

  useEffect(() => {
    persistMeetingDevicePrefs({
      micId: preferredMicDeviceId,
      speakerId: preferredSpeakerDeviceId,
      cameraId: preferredCameraDeviceId,
    });
  }, [persistMeetingDevicePrefs, preferredCameraDeviceId, preferredMicDeviceId, preferredSpeakerDeviceId]);

  const updateScheduleState = useCallback((startTime: Date | null) => {
    if (!startTime) {
      setIsBeforeScheduledStart(false);
      setScheduledCountdown("");
      return;
    }

    const diffMs = startTime.getTime() - Date.now();
    if (diffMs <= 0) {
      setIsBeforeScheduledStart(false);
      setScheduledCountdown("");
      return;
    }

    setIsBeforeScheduledStart(true);
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    if (hours) parts.push(`${hours}h`);
    parts.push(`${minutes.toString().padStart(2, '0')}m`);
    parts.push(`${seconds.toString().padStart(2, '0')}s`);
    setScheduledCountdown(parts.join(' '));
  }, []);

  const ensureOnline = useCallback(
    (action: string) => {
      if (!isOnline) {
        toast({
          title: "Offline mode",
          description: action,
          variant: "destructive",
        });
        return false;
      }
      return true;
    },
    [isOnline, toast]
  );

  const isHost = useMemo(() => Boolean(user && meeting?.host_id === user.id), [meeting?.host_id, user]);
  
  const audioAnalyzersRef = useRef<Map<string, AnalyserNode>>(new Map());
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const previousSpeakerRef = useRef<string | null>(null);
  const limitNotifiedRef = useRef<Set<string>>(new Set());
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const remoteAudioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const registerRemoteAudioElement = (peerId: string) => (element: HTMLAudioElement | null) => {
    if (!element) {
      remoteAudioRefs.current.delete(peerId);
      return;
    }

    remoteAudioRefs.current.set(peerId, element);
  };
  
  // Realtime channels
  const [participantsChannel, setParticipantsChannel] = useState<RealtimeChannel | null>(null);
  const [chatChannel, setChatChannel] = useState<RealtimeChannel | null>(null);
  const [meetingChannel, setMeetingChannel] = useState<RealtimeChannel | null>(null);
  const [reactionsChannel, setReactionsChannel] = useState<RealtimeChannel | null>(null);
  const [webinarChannel, setWebinarChannel] = useState<RealtimeChannel | null>(null);

  // Virtual background processing
  const { processedStream } = useVirtualBackground({
    videoStream: stream,
    background: virtualBackground
  });
  
  // Use processed stream for display and WebRTC when available
  const displayStream = processedStream || stream;

  // Keep the local video element in sync with the active display stream
  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) return;

    if (displayStream) {
      // Avoid unnecessary resets when the stream instance is unchanged
      if (videoElement.srcObject !== displayStream) {
        videoElement.srcObject = displayStream;
      }

      // Ensure track states mirror the current UI toggles
      displayStream.getVideoTracks().forEach(track => {
        track.enabled = isVideoEnabled;
      });

      displayStream.getAudioTracks().forEach(track => {
        track.enabled = isAudioEnabled;
      });
    } else {
      videoElement.srcObject = null;
    }
  }, [displayStream, isVideoEnabled, isAudioEnabled]);

  // WebRTC for voice - use processed stream for video sharing
  const currentUserId = user?.id || getGuestUserId();
  const { remoteStreams, peerConnections, connectionHealth, connectionError, clearConnectionError } = useWebRTC(resolvedMeetingId, currentUserId, displayStream);
  
  // Recording hook
  const { isRecording, recordingDuration, startRecording, stopRecording } = useRecording({
    meetingId: resolvedMeetingId || meetingId || '',
    stream: displayStream
  });

  const handleStartRecording = async () => {
    if (!ensureOnline("Recording can't start while offline.")) return;
    await startRecording();
    setMeeting((prev) => (prev ? { ...prev, is_recording: true } : prev));
  };

  const handleStopRecording = () => {
    if (!ensureOnline("Recording can't stop while offline.")) return;
    stopRecording();
    setMeeting((prev) => (prev ? { ...prev, is_recording: false } : prev));
  };

  useEffect(() => {
    const audioDevice = stream?.getAudioTracks()[0]?.getSettings().deviceId;
    const videoDevice = stream?.getVideoTracks()[0]?.getSettings().deviceId;

    if (audioDevice) {
      setPreferredMicDeviceId(audioDevice);
    }

    if (videoDevice) {
      setPreferredCameraDeviceId(videoDevice);
    }
  }, [stream]);

  useEffect(() => {
    if (!meetingId || meetingIdIsValid) return;

    const normalizedCodes = Array.from(
      new Set(
        [meetingId.trim().toUpperCase(), formatMeetingCode(meetingId.trim().toUpperCase())].filter(Boolean)
      )
    );

    let isActive = true;
    setResolvingMeetingLink(true);

    const resolveMeeting = async () => {
      for (const code of normalizedCodes) {
        const { data, error } = await supabase
          .from("meetings")
          .select("id")
          .eq("meeting_code", code)
          .maybeSingle();

        if (!isActive) return;

        if (data?.id) {
          navigate(`/meeting/${data.id}${window.location.search}`, { replace: true });
          setResolvingMeetingLink(false);
          return;
        }

        if (error && error.code !== "PGRST116") {
          console.error("[MeetingRoom] Failed to resolve meeting code from link:", error);
        }
      }

      toast({
        title: "Invalid meeting link",
        description: "We couldn't find this meeting. Try joining with the meeting code instead.",
        variant: "destructive",
      });
      setResolvingMeetingLink(false);
      const fallbackCode = encodeURIComponent(normalizedCodes[0] || meetingId);
      navigate(`/join/${fallbackCode}`, { replace: true });
    };

    resolveMeeting();

    return () => {
      isActive = false;
    };
  }, [meetingId, meetingIdIsValid, navigate, toast]);

  useEffect(() => {
    if (!meetingId) return;
    if (!meetingIdIsValid) return;

    if (showPreJoin) {
      // Fetch meeting data for pre-join screen
      fetchMeetingData();
    }

    return () => {
      cleanup();
    };
  }, [meetingId, meetingIdIsValid, showPreJoin]);

  useEffect(() => {
    if (connectionError) {
      transitionTo('reconnecting');
    } else if (meetingInitialized && (meetingState === 'connecting' || meetingState === 'reconnecting')) {
      transitionTo('live');
    }
  }, [connectionError, meetingInitialized, meetingState, transitionTo]);

  // Initialize meeting after audio check completes
  useEffect(() => {
    if (audioCheckCompleted && !showPreJoin && !showWaitingRoom && stream && !meetingInitialized && !loading) {
      console.log('[MeetingRoom] Audio check completed, initializing meeting...');
      initializeMeeting();
    }
  }, [audioCheckCompleted, showPreJoin, showWaitingRoom, stream, meetingInitialized, loading]);

  // Check if Picture-in-Picture is supported
  useEffect(() => {
    if (document.pictureInPictureEnabled) {
      setPipSupported(true);
    }
  }, []);

  useEffect(() => {
    updateScheduleState(scheduledStartTime);
    if (!scheduledStartTime) return;

    const interval = setInterval(() => updateScheduleState(scheduledStartTime), 1000);
    return () => clearInterval(interval);
  }, [scheduledStartTime, updateScheduleState]);

  // Setup PIP event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnterPip = () => setIsPipEnabled(true);
    const handleLeavePip = () => setIsPipEnabled(false);

    video.addEventListener('enterpictureinpicture', handleEnterPip);
    video.addEventListener('leavepictureinpicture', handleLeavePip);

    return () => {
      video.removeEventListener('enterpictureinpicture', handleEnterPip);
      video.removeEventListener('leavepictureinpicture', handleLeavePip);
    };
  }, []);

  useEffect(() => {
    if (isVideoEnabled && displayStream) {
      // Enable video tracks
      displayStream.getVideoTracks().forEach(track => {
        track.enabled = true;
      });
      if (videoRef.current) {
        videoRef.current.srcObject = displayStream;
      }
    } else if (displayStream) {
      // Disable video tracks but keep audio
      displayStream.getVideoTracks().forEach(track => {
        track.enabled = false;
      });
    }
  }, [isVideoEnabled, displayStream]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!flags.keyboardShortcuts) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = event.key.toLowerCase();

      switch (key) {
        case 'm':
          toggleAudio();
          trackShortcutUsed("toggle_audio", { key: 'm' }, userContext);
          break;
        case 'v':
          toggleVideo();
          trackShortcutUsed("toggle_video", { key: 'v' }, userContext);
          break;
        case 'g':
          toggleViewMode();
          trackShortcutUsed(
            "toggle_view",
            { key: 'g', nextView: viewMode === "default" ? "gallery" : "default" },
            userContext
          );
          toast({
            title: `Switched to ${viewMode === "default" ? "gallery" : "default"} view`,
          });
          break;
        case 'b':
          setBackgroundSelectorOpen(prev => !prev);
          trackShortcutUsed("toggle_background_selector", { key: 'b' }, userContext);
          break;
        case 'n':
          toggleNoiseSuppression();
          trackShortcutUsed("toggle_noise_suppression", { key: 'n' }, userContext);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    flags.keyboardShortcuts,
    toast,
    viewMode,
    userContext,
  ]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // If we've already collected a valid media stream during pre-join, skip the
    // extra audio check step so users land in the meeting faster. This prevents
    // the meeting from getting stuck in the connecting state when the audio
    // check dialog fails to appear (reported in user feedback).
    if (!showPreJoin && !showWaitingRoom && !audioCheckCompleted) {
      if (stream) {
        setAudioCheckCompleted(true);
        setShowAudioCheck(false);
      } else {
        setShowAudioCheck(true);
      }
    }
  }, [audioCheckCompleted, showPreJoin, showWaitingRoom, stream]);

  useEffect(() => {
    if (showPreJoin || showWaitingRoom) return;

    setStatusBarExpanded(true);
    if (statusBarTimerRef.current) {
      clearTimeout(statusBarTimerRef.current);
    }
    statusBarTimerRef.current = setTimeout(() => setStatusBarExpanded(false), 10000);

    return () => {
      if (statusBarTimerRef.current) {
        clearTimeout(statusBarTimerRef.current);
        statusBarTimerRef.current = null;
      }
    };
  }, [showPreJoin, showWaitingRoom]);

  const streamsReady = useMemo(() => {
    const localReady = Boolean(stream || displayStream);
    const remoteReady = remoteStreams.length > 0 || peerConnections.length > 0;
    return localReady && (isHost || remoteReady || isAdmitted);
  }, [displayStream, isAdmitted, isHost, peerConnections.length, remoteStreams.length, stream]);

  useEffect(() => {
    if (!showPreJoin && !showWaitingRoom && meeting && !isHost) {
      setJoiningOverlayVisible(true);
    }
  }, [isHost, meeting, showPreJoin, showWaitingRoom]);

  useEffect(() => {
    if (streamsReady) {
      setJoiningOverlayVisible(false);
    }
  }, [streamsReady]);

  useEffect(() => {
    if (meetingState === 'live' || meetingState === 'reconnecting' || meetingInitialized) {
      setJoiningOverlayVisible(false);
    }
  }, [meetingInitialized, meetingState]);

  useEffect(() => {
    if (!joiningOverlayVisible) {
      if (joiningOverlayTimeoutRef.current) {
        clearTimeout(joiningOverlayTimeoutRef.current);
        joiningOverlayTimeoutRef.current = null;
      }
      return;
    }

    if (joiningOverlayTimeoutRef.current) {
      clearTimeout(joiningOverlayTimeoutRef.current);
    }

    joiningOverlayTimeoutRef.current = setTimeout(() => {
      setJoiningOverlayVisible(false);
    }, 12000);

    return () => {
      if (joiningOverlayTimeoutRef.current) {
        clearTimeout(joiningOverlayTimeoutRef.current);
        joiningOverlayTimeoutRef.current = null;
      }
    };
  }, [joiningOverlayVisible]);

  useEffect(() => {
    const speakerKey = getSpeakerKey(activeSpeaker);
    const previousKey = getSpeakerKey(previousSpeakerRef.current);
    const now = Date.now();

    setSpeakingDurations((prev) => {
      const updated = new Map(prev);

      if (previousKey) {
        const previousEntry = updated.get(previousKey) || { totalMs: 0 };
        const elapsed = previousEntry.lastStarted ? now - previousEntry.lastStarted : 0;
        updated.set(previousKey, { totalMs: previousEntry.totalMs + elapsed, lastStarted: undefined });
      }

      if (speakerKey) {
        const currentEntry = updated.get(speakerKey) || { totalMs: 0 };
        updated.set(speakerKey, { totalMs: currentEntry.totalMs, lastStarted: now });
      }

      return updated;
    });

    previousSpeakerRef.current = activeSpeaker;
  }, [activeSpeaker, user?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSpeakingDurations((prev) => {
        const now = Date.now();
        const updated = new Map(prev);

        updated.forEach((entry, participantId) => {
          if (entry.lastStarted) {
            updated.set(participantId, {
              totalMs: entry.totalMs + (now - entry.lastStarted),
              lastStarted: now
            });
          }
        });

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Update elapsed time every second
  useEffect(() => {
    if (!joinedAtTime) return;

    const updateElapsedTime = () => {
      const now = Date.now();
      const diff = now - joinedAtTime;
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      const formatted = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      setElapsedTime(formatted);
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [joinedAtTime]);

  useEffect(() => {
    limitNotifiedRef.current.clear();
  }, [speakingLimitMinutes]);

  useEffect(() => {
    if (!speakingLimitMinutes) return;

    const limitMs = Number(speakingLimitMinutes) * 60 * 1000;
    const speakerKey = getSpeakerKey(activeSpeaker);

    if (!speakerKey) return;

    const duration = speakingDurations.get(speakerKey);

    if (duration && duration.totalMs >= limitMs && !limitNotifiedRef.current.has(speakerKey)) {
      limitNotifiedRef.current.add(speakerKey);
      toast({
        title: "Speaking limit reached",
        description: "The current speaker has passed the configured time limit.",
      });
    }
  }, [speakingDurations, speakingLimitMinutes, activeSpeaker]);

  // Handle remote audio streams
  useEffect(() => {
    remoteStreams.forEach(([peerId, stream]) => {
      const audioElement = remoteAudioRefs.current.get(peerId);

      if (!audioElement) {
        return;
      }

      if (audioElement.srcObject !== stream) {
        audioElement.srcObject = stream;
        audioElement.autoplay = true;
        audioElement.controls = false;
        audioElement.muted = false;
        // Ensure audio plays
        audioElement.play().catch(err => {
          console.error(`Error playing audio for peer ${peerId}:`, err);
        });
        console.log(`Set audio stream for peer ${peerId}, audio tracks:`, stream.getAudioTracks().length);
      }
    });
    
    // Clean up removed peers
    remoteAudioRefs.current.forEach((audioElement, peerId) => {
      const hasStream = remoteStreams.some(([id]) => id === peerId);
      if (!hasStream) {
        audioElement.pause();
        audioElement.srcObject = null;
        remoteAudioRefs.current.delete(peerId);
        console.log(`Removed audio element for peer ${peerId}`);
      }
    });
  }, [remoteStreams]);

  // Active speaker detection
  useEffect(() => {
    if (!stream && remoteStreams.length === 0) return;

    const audioContext = new AudioContext();
    const analyzers = new Map<string, AnalyserNode>();
    const audioLevels = new Map<string, number>();
    
    // Setup analyzer for local stream
    if (stream && isAudioEnabled) {
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyzer);
      analyzers.set('local', analyzer);
      audioLevels.set('local', 0);
    }
    
    // Setup analyzers for remote streams
    remoteStreams.forEach(([peerId, remoteStream]) => {
      try {
        const analyzer = audioContext.createAnalyser();
        analyzer.fftSize = 256;
        const source = audioContext.createMediaStreamSource(remoteStream);
        source.connect(analyzer);
        analyzers.set(peerId, analyzer);
        audioLevels.set(peerId, 0);
      } catch (error) {
        console.error(`Error creating analyzer for peer ${peerId}:`, error);
      }
    });
    
    audioAnalyzersRef.current = analyzers;
    
    // Monitor audio levels
    const monitorAudioLevels = () => {
      const dataArray = new Uint8Array(128);
      let maxLevel = 0;
      let maxSpeaker: string | null = null;
      
      analyzers.forEach((analyzer, id) => {
        analyzer.getByteFrequencyData(dataArray);
        
        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        audioLevels.set(id, average);
        
        // Find participant with highest audio level
        if (average > maxLevel && average > 30) { // Threshold to avoid noise
          maxLevel = average;
          maxSpeaker = id;
        }
      });
      
      // Update active speaker (with debounce to avoid flickering)
      if (maxSpeaker && maxLevel > 40) {
        setActiveSpeaker(maxSpeaker);
      } else if (maxLevel < 20) {
        setActiveSpeaker(null);
      }
    };
    
    const intervalId = setInterval(monitorAudioLevels, 100);
    
    return () => {
      clearInterval(intervalId);
      analyzers.forEach((analyzer) => {
        analyzer.disconnect();
      });
      audioContext.close();
    };
  }, [stream, remoteStreams, isAudioEnabled]);

  const handlePreJoinComplete = async (videoEnabled: boolean, audioEnabled: boolean, existingStream: MediaStream | null, guestName?: string) => {
    console.log('[MeetingRoom] Pre-join complete - video:', videoEnabled, 'audio:', audioEnabled);
    setIsVideoEnabled(videoEnabled);
    setIsAudioEnabled(audioEnabled);
    transitionTo('connecting');

    if (isBeforeScheduledStart && scheduledStartTime) {
      if (isHost) {
        try {
          if (!ensureOnline("You need to be online to start this meeting.")) return;

          const nowIso = new Date().toISOString();
          const { error } = await supabase
            .from("meetings")
            .update({
              is_active: true,
              scheduled_start: nowIso,
            })
            .eq("id", meetingId);

          if (error) throw error;

          const now = new Date();
          setScheduledStartTime(now);
          updateScheduleState(now);
          setMeeting(current => current ? { ...current, is_active: true, scheduled_start: nowIso } : current);
          toast({
            title: "Meeting started",
            description: "You started the meeting ahead of schedule.",
          });
        } catch (error) {
          console.error("[MeetingRoom] Error starting early:", error);
          transitionTo('prejoin');
          toast({
            title: "Unable to start meeting",
            description: "We couldn't start the meeting. Please try again.",
            variant: "destructive",
          });
          return;
        }
      } else {
        transitionTo('prejoin');
        toast({
          title: "Meeting not started",
          description: `This meeting is scheduled for ${format(scheduledStartTime, "PPpp")}. Please wait ${scheduledCountdown || 'a moment'} before joining.`,
          variant: "destructive",
        });
        return;
      }
    }
    
    // If guest, store guest name for this session with validation
    if (guestName && !user) {
      const validation = guestNameSchema.safeParse(guestName);
      if (validation.success) {
        sessionStorage.setItem('guestName', validation.data);
      }
    }
    
    setShowPreJoin(false);
    
    // Use the existing stream from PreJoinScreen instead of requesting a new one
    if (existingStream) {
      console.log('[MeetingRoom] Using existing media stream from PreJoinScreen');

      // Set track states based on user preferences
      existingStream.getVideoTracks().forEach(track => {
        track.enabled = videoEnabled;
      });
      existingStream.getAudioTracks().forEach(track => {
        track.enabled = audioEnabled;
      });
      
      setStream(existingStream);

      const audioDeviceId = existingStream.getAudioTracks()[0]?.getSettings().deviceId;
      const videoDeviceId = existingStream.getVideoTracks()[0]?.getSettings().deviceId;

      if (audioDeviceId) {
        setPreferredMicDeviceId(audioDeviceId);
      }

      if (videoDeviceId) {
        setPreferredCameraDeviceId(videoDeviceId);
      }

      if (videoRef.current) {
        videoRef.current.srcObject = existingStream;
      }
      
      console.log('[MeetingRoom] Media stream ready');
    } else {
      console.warn('[MeetingRoom] No stream from PreJoinScreen, requesting new one');
      // Fallback: request new stream if none was passed
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoEnabled ? getVideoConstraints() : false,
          audio: {
            ...(preferredMicDeviceId ? { deviceId: { exact: preferredMicDeviceId } } : {}),
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        
        mediaStream.getVideoTracks().forEach(track => {
          track.enabled = videoEnabled;
        });
        mediaStream.getAudioTracks().forEach(track => {
          track.enabled = audioEnabled;
        });

        const audioDeviceId = mediaStream.getAudioTracks()[0]?.getSettings().deviceId;
        const videoDeviceId = mediaStream.getVideoTracks()[0]?.getSettings().deviceId;

        if (audioDeviceId) {
          setPreferredMicDeviceId(audioDeviceId);
        }

        if (videoDeviceId) {
          setPreferredCameraDeviceId(videoDeviceId);
        }

        setStream(mediaStream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        
        console.log('[MeetingRoom] New media stream started');
      } catch (error: any) {
        console.error('[MeetingRoom] Error starting media:', error);
        toast({
          title: "Camera/Microphone Error",
          description: "Could not access camera or microphone. Please check permissions and try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleAudioCheckConfirm = async ({ micDeviceId, speakerDeviceId }: { micDeviceId?: string; speakerDeviceId?: string }) => {
    if (micDeviceId) {
      setPreferredMicDeviceId(micDeviceId);
      localStorage.setItem("preferredMicId", micDeviceId);
      await applyMicDevice(micDeviceId);
    }

    if (speakerDeviceId) {
      setPreferredSpeakerDeviceId(speakerDeviceId);
      localStorage.setItem("preferredSpeakerId", speakerDeviceId);
    }

    setAudioCheckCompleted(true);
    setShowAudioCheck(false);
  };

  const handleAudioCheckSkip = () => {
    setAudioCheckCompleted(true);
    setShowAudioCheck(false);
  };

  const fetchMeetingData = async () => {
    if (!meetingIdIsValid || !meetingId) return;

    try {
      const { data: meetingDataRaw, error: meetingError } = await supabase
        .from("meetings")
        .select("id, title, description, meeting_code, host_id, is_active, is_recording, waiting_room_enabled, is_webinar_mode, scheduled_start, scheduled_end")
        .eq("id", meetingId)
        .maybeSingle();

      if (meetingError) {
        console.error("Error fetching meeting:", meetingError);
        toast({
          title: "Error loading meeting",
          description: "Unable to load meeting details. Please try again.",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      if (!meetingDataRaw) {
        toast({
          title: "Meeting not found",
          description: "This meeting doesn't exist or you don't have access. Please use the meeting link or code provided by the host.",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      let meetingData = meetingDataRaw;

      const startDate = meetingData.scheduled_start ? new Date(meetingData.scheduled_start) : null;
      const isFutureStart = !!(startDate && startDate.getTime() > Date.now());

      if (!meetingData.is_active && !isFutureStart) {
        toast({
          title: "Meeting ended",
          description: "This meeting has ended and can't be joined again.",
          variant: "destructive",
        });
        transitionTo('ended');
        navigate("/");
        return;
      }

      setMeeting(meetingData);

      setScheduledStartTime(startDate);
      updateScheduleState(startDate);

      if (isFutureStart) {
        toast({
          title: "Meeting hasn't started yet",
          description: `Scheduled for ${format(startDate, "PPpp")}. We'll let you in once it begins.`,
        });
      }
      
      // Set webinar mode from meeting data
      setIsWebinarMode(meetingData.is_webinar_mode);

      // Fetch host display name for consent prompts
      const { data: hostProfile } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", meetingData.host_id)
        .maybeSingle();

      if (hostProfile?.display_name) {
        setHostName(hostProfile.display_name);
      }
      
      // Check if user is the host first (hosts bypass waiting room)
      const isHost = user && meetingData.host_id === user.id;
      
      // Only apply waiting room logic for authenticated non-host users
      // Guests can join directly without waiting room
      if (meetingData.waiting_room_enabled && !isHost && user) {
        // Check if user is already admitted
        const { data: queueEntry } = await supabase
          .from('waiting_room_queue')
          .select('status')
          .eq('meeting_id', meetingId)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!queueEntry || queueEntry.status === 'waiting') {
          // Clean up old waiting/denied entries before showing waiting room
          await supabase
            .from('waiting_room_queue')
            .delete()
            .eq('meeting_id', meetingId)
            .eq('user_id', user.id)
            .in('status', ['waiting', 'denied']);
          setShowWaitingRoom(true);
        } else if (queueEntry.status === 'admitted') {
          // User is already admitted, let them rejoin
          setIsAdmitted(true);
        } else if (queueEntry.status === 'denied') {
          toast({
            title: "Access denied",
            description: "You were denied access to this meeting",
            variant: "destructive"
          });
          navigate("/");
          return;
        }
      } else if (isHost) {
        console.log("Host detected, bypassing waiting room");
      }
      
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching meeting:", error);
      toast({
        title: "Error loading meeting",
        description: error.message,
        variant: "destructive",
      });
      navigate("/");
    }
  };

  const initializeMeeting = async () => {
    if (meetingInitialized) {
      console.log('[MeetingRoom] Already initialized, skipping...');
      return;
    }
    
    try {
      setMeetingInitialized(true);
      
      // Join the meeting as a participant
      await joinMeeting();
      
      // Set up real-time subscriptions
      setupRealtimeSubscriptions();
      
      // Fetch initial data
      await Promise.all([
        fetchParticipants(),
        fetchMessages(),
        fetchRaisedHands()
      ]);

      setLoading(false);
      transitionTo('live');
    } catch (error: any) {
      console.error("Error initializing meeting:", error);
      setMeetingInitialized(false); // Reset on error so it can be retried
      transitionTo('ended');
      toast({
        title: "Error joining meeting",
        description: error.message,
        variant: "destructive",
      });
      navigate("/");
    }
  };

  const getVideoConstraints = () => {
    const qualitySettings = {
      low: { width: 320, height: 240, frameRate: 15 },
      medium: { width: 640, height: 480, frameRate: 24 },
      high: { width: 1280, height: 720, frameRate: 30 }
    };

    return {
      facingMode: facingMode,
      ...(preferredCameraDeviceId ? { deviceId: { exact: preferredCameraDeviceId } } : {}),
      ...qualitySettings[videoQuality]
    };
  };

  const startMediaStream = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: getVideoConstraints(),
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      // Set video track state based on user preference
      mediaStream.getVideoTracks().forEach(track => {
        track.enabled = isVideoEnabled;
      });
      // Audio tracks should respect the isAudioEnabled state but always be available
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = isAudioEnabled;
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      console.log("Media stream started successfully with audio enabled:", isAudioEnabled);
    } catch (error: any) {
      console.error("Error accessing media:", error);
      toast({
        title: "Media access denied",
        description: "Please allow camera and microphone access for voice communication.",
        variant: "destructive",
      });
    }
  };

  const joinMeeting = async () => {
    if (!meetingId) return;

    const guestName = !user ? sessionStorage.getItem('guestName') : null;
    const userId = user?.id || getGuestUserId();

    const participantRole = user && meeting?.host_id === user.id ? 'host' : 'participant';

    const participantData: any = {
      meeting_id: meetingId,
      user_id: userId,
      is_video_enabled: isVideoEnabled,
      is_audio_enabled: isAudioEnabled,
      is_screen_sharing: isScreenSharing,
      role: participantRole,
      left_at: null
    };

    if (guestName) {
      participantData.guest_name = guestName;
    }

    const { error } = await supabase
      .from("meeting_participants")
      .upsert(participantData, {
        onConflict: 'meeting_id,user_id'
      });

    if (error) {
      console.error("Error joining meeting:", error);
      
      // Check if this is a rate limit error
      const isRateLimit = handleRateLimitError(error, 'meeting_join');
      
      if (!isRateLimit) {
        toast({
          title: "Error joining meeting",
          description: error.message,
          variant: "destructive",
        });
      }
      throw error;
    }

    console.log("Successfully joined meeting as participant");
  };

  const setupRealtimeSubscriptions = () => {
    if (!meetingId) return;

    // Participants subscription
    const participantsChannel = supabase
      .channel(`meeting-participants-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meeting_participants',
          filter: `meeting_id=eq.${meetingId}`
        },
        (payload: any) => {
          // Show toast notification when a guest updates their display name
          if (payload.eventType === 'UPDATE' && payload.new.guest_name && payload.old.guest_name !== payload.new.guest_name) {
            const oldName = payload.old.guest_name || 'Guest';
            const newName = payload.new.guest_name;
            toast({
              title: "Display name updated",
              description: `${oldName} changed their name to ${newName}`,
            });
          }
          
          // Show toast notification when a guest updates their avatar
          if (payload.eventType === 'UPDATE' && payload.new.avatar_url && payload.old.avatar_url !== payload.new.avatar_url) {
            const guestName = payload.new.guest_name || 'A participant';
            toast({
              title: "Profile picture updated",
              description: `${guestName} updated their profile picture`,
            });
          }
          
          fetchParticipants();
        }
      )
      .subscribe();

    const webinarChannel = supabase
      .channel(`meeting-raised-hands-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'raised_hands',
          filter: `meeting_id=eq.${meetingId}`
        },
        (payload) => {
          console.log('Raised hands change:', payload);
          fetchRaisedHands();
        }
      )
      .subscribe();

    // Chat subscription
    const chatChannel = supabase
      .channel(`meeting-chat-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `meeting_id=eq.${meetingId}`
        },
        (payload) => {
          const message = (payload as any)?.new;
          if (!message) return;

          hydrateMessage(message).then((hydrated) => {
            if (!hydrated) return;

            if (hydrated.message_type === 'reaction') {
              const payload = parseChatPayload(hydrated.message);
              if (payload?.messageId && payload?.emoji) {
                applyReactionToMessages(payload.messageId, payload.emoji, hydrated.user_id);
              }
              return;
            }

            setMessages((prev) => {
              if (prev.some((msg) => msg.id === hydrated.id)) return prev;
              return [...prev, { ...hydrated, reactions: hydrated.reactions || {} }];
            });
          });
        }
      )
      .subscribe();

    // Meeting status subscription
    const meetingChannel = supabase
      .channel(`meeting-status-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'meetings',
          filter: `id=eq.${meetingId}`
        },
        (payload: any) => {
          if (payload.new) {
            setMeeting(payload.new as Meeting);

            const updatedStart = payload.new.scheduled_start ? new Date(payload.new.scheduled_start) : null;
            setScheduledStartTime(updatedStart);
            updateScheduleState(updatedStart);
            
            // Handle webinar mode changes
            if (payload.old?.is_webinar_mode !== payload.new.is_webinar_mode) {
              setIsWebinarMode(payload.new.is_webinar_mode);
              
              if (payload.new.is_webinar_mode && user?.id !== payload.new.host_id) {
                enforceAttendeeMute();
                toast({
                  title: "Webinar mode enabled",
                  description: "You are now in view-only mode. Raise your hand to speak.",
                });
              }
            }

            if (!payload.new.is_active) {
              toast({
                title: "Meeting ended",
                description: "The host has ended this meeting.",
                variant: "destructive",
              });
              cleanup();
              navigate("/");
            }

            if (
              payload.old?.is_recording !== payload.new.is_recording &&
              user?.id !== payload.new.host_id
            ) {
              toast({
                title: payload.new.is_recording ? "Recording started" : "Recording stopped",
                description: payload.new.is_recording
                  ? "The host has started recording this meeting."
                  : "Recording has been stopped.",
              });
            }
          }
        }
      )
      .subscribe();

    // Reactions channel for broadcasting emoji reactions
    const reactionsChannel = supabase
      .channel(`meeting-reactions-${meetingId}`)
      .on('broadcast', { event: 'reaction' }, ({ payload }) => {
        const reaction = {
          id: crypto.randomUUID(),
          emoji: payload.emoji,
          x: Math.random() * 80 + 10, // Random position between 10% and 90%
          timestamp: Date.now()
        };
        setReactions(prev => [...prev, reaction]);
      })
      .subscribe();

    setParticipantsChannel(participantsChannel);
    setChatChannel(chatChannel);
    setMeetingChannel(meetingChannel);
    setReactionsChannel(reactionsChannel);
    setWebinarChannel(webinarChannel);
  };

  const fetchParticipants = async () => {
    const { data, error } = await supabase
      .from("meeting_participants")
      .select("*")
      .eq("meeting_id", meetingId)
      .is("left_at", null);

    if (error) {
      console.error("Error fetching participants:", error);
      return;
    }

    // Fetch profiles separately
    const participantsWithProfiles = await Promise.all(
      (data || []).map(async (participant) => {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, avatar_url")
          .eq("user_id", participant.user_id)
          .single();
        
        return { ...participant, profiles: profile };
      })
    );

    setParticipants(participantsWithProfiles);
    
    // Set meeting joined time from current user's joined_at
    const currentUserId = user?.id || getGuestUserId();
    const currentParticipant = data?.find(p => p.user_id === currentUserId);
    if (currentParticipant?.joined_at && !joinedAtTime) {
      setJoinedAtTime(new Date(currentParticipant.joined_at).getTime());
    }
    
    // Update participant info map for remote video display
    const infoMap = new Map();
    participantsWithProfiles.forEach(p => {
      const displayName = p.guest_name || p.profiles?.display_name || 'Unknown';
      infoMap.set(p.user_id, {
        name: displayName,
        isVideoEnabled: p.is_video_enabled,
        isAudioEnabled: p.is_audio_enabled
      });
    });
    setParticipantInfoMap(infoMap);
  };

  const parseChatPayload = (raw: unknown) => {
    if (!raw || typeof raw !== 'string') return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('Unable to parse chat payload', error);
      return null;
    }
  };

  const applyReactionToList = (list: MeetingChatMessage[], messageId: string, emoji: string, userId: string) => {
    return list.map((msg) => {
      if (msg.id !== messageId) return msg;
      const reactions = { ...(msg.reactions || {}) };
      const nextReactors = new Set(reactions[emoji] || []);
      nextReactors.add(userId);
      return { ...msg, reactions: { ...reactions, [emoji]: Array.from(nextReactors) } };
    });
  };

  const applyReactionToMessages = useCallback(
    (messageId: string, emoji: string, userId: string) => {
      setMessages((prev) => applyReactionToList(prev, messageId, emoji, userId));
    },
    []
  );

  const hydrateMessage = useCallback(async (message: any): Promise<MeetingChatMessage | null> => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", message.user_id)
      .maybeSingle();

    const baseMessage: MeetingChatMessage = {
      ...message,
      profiles: profile || message.profiles || null,
      reactions: message.reactions || {},
      attachment: null,
    };

    if (message.message_type === 'reaction') {
      return baseMessage;
    }

    if (message.message_type === 'file') {
      const payload = parseChatPayload(message.message);
      if (payload?.url) {
        return {
          ...baseMessage,
          attachment: {
            url: payload.url,
            name: payload.name || 'Shared file',
            size: payload.size,
            type: payload.type,
            caption: payload.caption,
          },
          message: payload.caption || payload.name || 'Shared a file',
        };
      }
    }

    return baseMessage;
  }, []);

  const fetchMessages = useCallback(async () => {
    const { data, error } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("meeting_id", meetingId)
      .order("created_at", { ascending: true })
      .limit(100);

    if (error) {
      console.error("Error fetching messages:", error);
      return;
    }

    const messagesWithProfiles = await Promise.all((data || []).map(hydrateMessage));

    const reactionQueue: Array<{ target: string; emoji: string; userId: string }> = [];
    const displayMessages: MeetingChatMessage[] = [];

    messagesWithProfiles.forEach((msg) => {
      if (!msg) return;
      if (msg.message_type === 'reaction') {
        const payload = parseChatPayload(msg.message);
        if (payload?.messageId && payload?.emoji) {
          reactionQueue.push({ target: payload.messageId, emoji: payload.emoji, userId: msg.user_id });
        }
        return;
      }
      displayMessages.push({ ...msg, reactions: msg.reactions || {} });
    });

    const withReactions = reactionQueue.reduce(
      (list, reaction) => applyReactionToList(list, reaction.target, reaction.emoji, reaction.userId),
      displayMessages
    );

    setMessages(withReactions);
  }, [hydrateMessage, meetingId]);

  const sendMessage = async () => {
    if (!currentMessage.trim() || !meetingId) return;

    const now = Date.now();
    chatTimestampsRef.current = chatTimestampsRef.current.filter(ts => now - ts < 10000);
    if (chatTimestampsRef.current.length >= 5) {
      toast({
        title: "You're sending messages too quickly",
        description: "Please wait a few seconds before sending more chat messages.",
        variant: "destructive",
      });
      return;
    }

    const validation = chatMessageSchema.safeParse(currentMessage);
    if (!validation.success) {
      toast({
        title: "Invalid message",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    const userId = user?.id || getGuestUserId();
    const guestName = !user ? sessionStorage.getItem('guestName') : null;

    const messageData: any = {
      meeting_id: meetingId,
      user_id: userId,
      message: validation.data,
      message_type: 'text',
    };

    if (guestName) {
      messageData.guest_name = guestName;
    }

    const pendingDisplay: MeetingChatMessage = {
      id: crypto.randomUUID(),
      user_id: userId,
      message: validation.data,
      guest_name: guestName ?? undefined,
      created_at: new Date().toISOString(),
      profiles: {
        display_name: user?.user_metadata?.display_name || guestName || "You",
      },
      isPending: true,
      reactions: {},
      attachment: null,
      message_type: 'text',
    };

    if (!isOnline) {
      savePendingEdit({
        type: PENDING_CHAT_TYPE,
        payload: { meetingId, message: messageData, display: pendingDisplay },
      });
      setMessages((prev) => [...prev, pendingDisplay]);
      setCurrentMessage("");
      toast({
        title: "Saved offline",
        description: "We'll sync this chat message once you're back online.",
      });
      return;
    }

    const { data, error } = await supabase
      .from("chat_messages")
      .insert(messageData)
      .select()
      .single();

    if (error) {
      const isRateLimit = handleRateLimitError(error, 'chat_message');

      if (!isRateLimit) {
        toast({
          title: "Error sending message",
          description: error.message,
          variant: "destructive",
        });
      }
      return;
    }

    chatTimestampsRef.current.push(now);
    setCurrentMessage("");

    if (data) {
      const hydrated = await hydrateMessage(data);
      if (hydrated) {
        setMessages((prev) => [...prev, { ...hydrated, reactions: hydrated.reactions || {} }]);
      }
    }
  };

  const sendChatReaction = async (messageId: string, emoji: string) => {
    if (!meetingId) return;

    const userId = user?.id || getGuestUserId();
    const guestName = !user ? sessionStorage.getItem('guestName') : null;

    applyReactionToMessages(messageId, emoji, userId);

    const reactionPayload = JSON.stringify({ messageId, emoji });
    const { error } = await supabase.from('chat_messages').insert({
      meeting_id: meetingId,
      user_id: userId,
      guest_name: guestName,
      message: reactionPayload,
      message_type: 'reaction',
    });

    if (error) {
      console.error('Failed to send reaction', error);
      toast({
        title: "Couldn't send reaction",
        description: "Please try again in a moment.",
      });
    }
  };

  const sendFileAttachment = async (file: File) => {
    if (!meetingId) return;

    if (!isOnline) {
      toast({
        title: "You're offline",
        description: "Reconnect to share files in the chat.",
        variant: "destructive",
      });
      return;
    }

    setUploadingFile(true);
    const userId = user?.id || getGuestUserId();
    const guestName = !user ? sessionStorage.getItem('guestName') : null;

    try {
      const path = `${meetingId}/${Date.now()}-${file.name}`;
      const { data: storageResult, error: storageError } = await supabase.storage
        .from('meeting-files')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (storageError || !storageResult?.path) {
        throw storageError || new Error('Upload failed');
      }

      const { data: publicData } = supabase.storage.from('meeting-files').getPublicUrl(storageResult.path);
      const publicUrl = publicData?.publicUrl;

      if (!publicUrl) {
        throw new Error('Unable to generate file link');
      }

      const payload = {
        url: publicUrl,
        name: file.name,
        size: file.size,
        type: file.type,
      };

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          meeting_id: meetingId,
          user_id: userId,
          guest_name: guestName,
          message: JSON.stringify(payload),
          message_type: 'file',
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        const hydrated = await hydrateMessage(data);
        if (hydrated) {
          setMessages((prev) => [...prev, { ...hydrated, reactions: hydrated.reactions || {} }]);
        }
      }

      toast({
        title: "File shared",
        description: `${file.name} posted to the meeting chat.`,
      });
    } catch (error) {
      console.error('File upload failed', error);
      toast({
        title: "Couldn't share file",
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    if (!meetingId || !pendingEdits.length) return;

    const offlineMessages = pendingEdits
      .filter((edit) => edit.type === PENDING_CHAT_TYPE)
      .map((edit) => {
        const payload = edit.payload as { meetingId: string; message: any; display?: MeetingChatMessage };
        if (!payload || payload.meetingId !== meetingId) return null;
        if (payload.display) return payload.display;

        return {
          id: edit.id,
          user_id: payload.message.user_id,
          message: payload.message.message,
          guest_name: payload.message.guest_name,
          created_at: payload.message.created_at || new Date().toISOString(),
          profiles: { display_name: payload.message.guest_name || "You" },
          isPending: true,
          attachment: null,
          reactions: {},
          message_type: payload.message.message_type,
        } as MeetingChatMessage;
      })
      .filter(Boolean) as MeetingChatMessage[];

    if (offlineMessages.length) {
      setMessages((prev) => {
        const existing = new Set(prev.filter((msg) => msg.isPending).map((msg) => msg.id));
        const merged = [...prev];
        offlineMessages.forEach((msg) => {
          if (!existing.has(msg.id)) {
            merged.push(msg);
          }
        });
        return merged;
      });
    }
  }, [meetingId, pendingEdits]);

  useEffect(() => {
    if (!isOnline || !meetingId) return;

    const pendingMessages = consumePendingEdits(
      PENDING_CHAT_TYPE,
      (edit) => {
        const payload = edit.payload as { meetingId: string } | undefined;
        return payload?.meetingId === meetingId;
      }
    );
    if (!pendingMessages.length) return;

    const syncMessages = async () => {
      for (const edit of pendingMessages) {
        const payload = edit.payload as { meetingId: string; message: any; display?: MeetingChatMessage };
        if (!payload || payload.meetingId !== meetingId) continue;

        try {
          await supabase.from("chat_messages").insert(payload.message);
        } catch (error) {
          console.error("Failed to sync offline message", error);
          savePendingEdit({ type: PENDING_CHAT_TYPE, payload });
        }
      }

      setMessages((prev) => prev.filter((msg) => !msg.isPending));
      fetchMessages();
    };

    syncMessages();
  }, [consumePendingEdits, fetchMessages, isOnline, meetingId, savePendingEdit]);

  const toggleVideo = async () => {
    if (!canControlLiveMedia) {
      toast({
        title: "Joining meeting...",
        description: "Video controls become available after you connect.",
      });
      return;
    }

    if (isWebinarMode && meeting && user?.id !== meeting.host_id) {
      toast({
        title: "Webinar mode is on",
        description: "Only hosts can turn on video while webinar mode is active.",
        variant: "destructive",
      });
      return;
    }

    const newVideoState = !isVideoEnabled;
    setIsVideoEnabled(newVideoState);

    const updateVideoTracks = (targetStream: MediaStream | null) => {
      if (!targetStream) return;

      targetStream.getVideoTracks().forEach(track => {
        track.enabled = newVideoState;
      });

      if (newVideoState && videoRef.current && !videoRef.current.srcObject) {
        videoRef.current.srcObject = targetStream;
      }
    };

    updateVideoTracks(stream);
    if (displayStream !== stream) {
      updateVideoTracks(displayStream);
    }

    // Ensure sender tracks respect the new state for all peers
    peerConnections.forEach(([peerId, peerConnection]) => {
      const senders = peerConnection.connection.getSenders();
      const videoSender = senders.find(sender => sender.track?.kind === 'video');

      if (videoSender?.track) {
        videoSender.track.enabled = newVideoState;
        console.log(`Updated video track for peer ${peerId}: ${newVideoState ? 'enabled' : 'disabled'}`);
      }
    });

    if (meetingId) {
      const userId = user?.id || getGuestUserId();
      await supabase
        .from("meeting_participants")
        .update({ is_video_enabled: newVideoState })
        .eq("meeting_id", meetingId)
        .eq("user_id", userId);
    }
  };

  const toggleAudio = async () => {
    if (!canControlLiveMedia) {
      toast({
        title: "Joining meeting...",
        description: "Audio controls become available after you connect.",
      });
      return;
    }

    if (!canUserUnmute()) {
      toast({
        title: "Webinar mode is on",
        description: "Ask the host to enable your microphone.",
        variant: "destructive",
      });
      return;
    }

    const newAudioState = !isAudioEnabled;
    setIsAudioEnabled(newAudioState);
    
    // Update audio tracks in both stream and displayStream
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = newAudioState;
        console.log(`Local audio track ${newAudioState ? 'enabled' : 'disabled'}`);
      });
    }
    
    if (displayStream && displayStream !== stream) {
      displayStream.getAudioTracks().forEach(track => {
        track.enabled = newAudioState;
      });
    }
    
    // Update all peer connections with the new audio state
    peerConnections.forEach(([peerId, peerConnection]) => {
      const senders = peerConnection.connection.getSenders();
      const audioSender = senders.find(sender => sender.track?.kind === 'audio');

      if (audioSender && audioSender.track) {
        audioSender.track.enabled = newAudioState;
        console.log(`Updated audio track for peer ${peerId}: ${newAudioState ? 'enabled' : 'disabled'}`);
      }
    });
    
    if (meetingId) {
      const userId = user?.id || getGuestUserId();
      await supabase
        .from("meeting_participants")
        .update({ is_audio_enabled: newAudioState })
        .eq("meeting_id", meetingId)
        .eq("user_id", userId);
    }
    
    toast({
      title: newAudioState ? "Microphone on" : "Microphone muted",
      description: newAudioState ? "You can now be heard" : "You are muted",
    });
  };

  const toggleNoiseSuppression = async () => {
    if (!stream) {
      toast({
        title: "No audio stream",
        description: "Please enable your microphone first",
        variant: "destructive",
      });
      return;
    }

    const newState = !noiseSuppressionEnabled;
    setNoiseSuppressionEnabled(newState);

    if (newState) {
      // Enable noise suppression using Web Audio API
      try {
        const audioContext = new AudioContext();
        const audioTracks = stream.getAudioTracks();
        
        if (audioTracks.length === 0) {
          throw new Error("No audio tracks available");
        }

        // Create a new stream with just the audio track
        const audioStream = new MediaStream([audioTracks[0]]);
        
        // Create audio nodes
        const source = audioContext.createMediaStreamSource(audioStream);
        const gainNode = audioContext.createGain();
        const destination = audioContext.createMediaStreamDestination();
        
        // Create a simple noise gate effect
        // This reduces gain for low-level signals (background noise)
        gainNode.gain.value = 1.0;
        
        // Connect the nodes
        source.connect(gainNode);
        gainNode.connect(destination);
        
        // Store references for cleanup
        audioContextRef.current = audioContext;
        sourceNodeRef.current = source;
        gainNodeRef.current = gainNode;
        destinationRef.current = destination;
        
        // Replace the audio track in the original stream
        const processedTrack = destination.stream.getAudioTracks()[0];
        const originalTrack = audioTracks[0];
        
        stream.removeTrack(originalTrack);
        stream.addTrack(processedTrack);
        
        toast({
          title: "Noise suppression enabled",
          description: "Background noise will be reduced",
        });
      } catch (error) {
        console.error("Error enabling noise suppression:", error);
        setNoiseSuppressionEnabled(false);
        toast({
          title: "Error",
          description: "Failed to enable noise suppression",
          variant: "destructive",
        });
      }
    } else {
      // Disable noise suppression
      try {
        // Clean up audio nodes
        if (sourceNodeRef.current) {
          sourceNodeRef.current.disconnect();
        }
        if (gainNodeRef.current) {
          gainNodeRef.current.disconnect();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        
        // Reset references
        audioContextRef.current = null;
        sourceNodeRef.current = null;
        gainNodeRef.current = null;
        destinationRef.current = null;
        
        // Restart the audio stream without processing
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const newAudioTrack = audioStream.getAudioTracks()[0];
        newAudioTrack.enabled = isAudioEnabled;
        
        // Replace track in stream
        const oldAudioTrack = stream.getAudioTracks()[0];
        if (oldAudioTrack) {
          stream.removeTrack(oldAudioTrack);
          oldAudioTrack.stop();
        }
        stream.addTrack(newAudioTrack);
        
        toast({
          title: "Noise suppression disabled",
          description: "Using original audio",
        });
      } catch (error) {
        console.error("Error disabling noise suppression:", error);
        toast({
          title: "Error",
          description: "Failed to disable noise suppression",
          variant: "destructive",
        });
      }
    }
  };

  const applyMicDevice = useCallback(async (deviceId?: string) => {
    if (!deviceId) return;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const [newTrack] = newStream.getAudioTracks();
      if (!newTrack) return;

      newTrack.enabled = isAudioEnabled;

      const videoTracks = stream?.getVideoTracks() || [];
      const combinedStream = new MediaStream([...videoTracks, newTrack]);

      // Stop old audio tracks
      if (stream) {
        stream.getAudioTracks().forEach(track => track.stop());
      }

      setStream(combinedStream);

      if (videoRef.current) {
        videoRef.current.srcObject = combinedStream;
      }

      peerConnections.forEach(([peerId, peerConnection]) => {
        const sender = peerConnection.connection.getSenders().find(s => s.track?.kind === 'audio');
        if (sender) {
          sender.replaceTrack(newTrack);
        } else {
          peerConnection.connection.addTrack(newTrack, combinedStream);
        }
        console.log(`Applied new microphone to peer ${peerId}`);
      });
    } catch (error: any) {
      console.error("Error applying microphone device:", error);
      toast({
        title: "Microphone error",
        description: error?.message || "Unable to switch microphone.",
        variant: "destructive",
      });
    }
  }, [isAudioEnabled, peerConnections, stream, toast]);

  const toggleScreenShare = async () => {
    if (!canControlLiveMedia) {
      toast({
        title: "Joining meeting...",
        description: "Screen sharing is available once you're live in the meeting.",
      });
      return;
    }

    if (isWebinarMode && meeting && user?.id !== meeting.host_id) {
      toast({
        title: "Webinar mode is on",
        description: "Only hosts can share screens while webinar mode is active.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!isScreenSharing) {
        // Start screen sharing
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false
        });
        
        // Replace video track in existing stream
        if (stream) {
          const screenTrack = screenStream.getVideoTracks()[0];
          const videoTrack = stream.getVideoTracks()[0];
          
          // Replace track in peer connections
          if (videoTrack) {
            stream.removeTrack(videoTrack);
            videoTrack.stop();
          }
          stream.addTrack(screenTrack);
          
          // Trigger React update so peer connections receive new track
          setStream(new MediaStream(stream.getTracks()));
          
          // Update video element
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          
          // Handle when user stops sharing via browser UI
          screenTrack.onended = () => {
            toggleScreenShare();
          };
        }
        
        setIsScreenSharing(true);
      } else {
        // Stop screen sharing and return to camera
        if (stream) {
          const screenTrack = stream.getVideoTracks()[0];
          if (screenTrack) {
            screenTrack.stop();
            stream.removeTrack(screenTrack);
          }
          
          // Get camera stream again
          const cameraStream = await navigator.mediaDevices.getUserMedia({
            video: getVideoConstraints(),
            audio: false
          });
          
          const cameraTrack = cameraStream.getVideoTracks()[0];
          cameraTrack.enabled = isVideoEnabled;
          stream.addTrack(cameraTrack);
          
          // Trigger React update so peer connections receive new track
          setStream(new MediaStream(stream.getTracks()));
          
          // Update video element
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }
        
        setIsScreenSharing(false);
      }
      
      if (meetingId) {
        const userId = user?.id || getGuestUserId();
        await supabase
          .from("meeting_participants")
          .update({ is_screen_sharing: !isScreenSharing })
          .eq("meeting_id", meetingId)
          .eq("user_id", userId);
      }
    } catch (error: any) {
      console.error("Error toggling screen share:", error);
      toast({
        title: "Screen share error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const leaveMeeting = async () => {
    if (meetingId) {
      const userId = user?.id || getGuestUserId();

      try {
        if (user && meeting && user.id === meeting.host_id) {
          await supabase.from("meetings").update({ is_active: false }).eq("id", meetingId);
        }

        await supabase
          .from("meeting_participants")
          .update({ left_at: new Date().toISOString() })
          .eq("meeting_id", meetingId)
          .eq("user_id", userId);
      } catch (error) {
        console.error("Failed to leave meeting cleanly", error);
      }
    }

    transitionTo('ended');
    cleanup();
    navigate("/");
  };

  const handleLeaveClick = () => {
    setShowLeaveConfirmation(true);
  };

  const confirmLeave = () => {
    setShowLeaveConfirmation(false);
    leaveMeeting();
  };

  const endMeeting = async () => {
    if (!user || !meeting || user.id !== meeting.host_id) {
      toast({
        title: "Permission denied",
        description: "Only the host can end the meeting.",
        variant: "destructive",
      });
      return;
    }

    if (!ensureOnline("End meeting is unavailable while offline.")) return;

    try {
      const { error } = await supabase
        .from("meetings")
        .update({ is_active: false })
        .eq("id", meetingId);

      if (error) throw error;

      toast({
        title: "Meeting ended",
        description: "The meeting has been ended successfully.",
      });

      transitionTo('ended');
      cleanup();
      navigate("/");
    } catch (error) {
      console.error("Error ending meeting:", error);
      toast({
        title: "Error",
        description: "Failed to end the meeting.",
        variant: "destructive",
      });
    }
  };

  const copyMeetingCode = async () => {
    if (!meeting?.meeting_code) return;

    try {
      await navigator.clipboard.writeText(meeting.meeting_code);
      toast({
        title: "Copied!",
        description: "Meeting code copied to clipboard",
      });
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast({
        title: "Error",
        description: "Failed to copy meeting code",
        variant: "destructive",
      });
    }
  };

  const copyJoinLink = async () => {
    if (!meeting?.meeting_code) return;
    const joinLink = getMeetingLink(meeting.meeting_code);

    try {
      await navigator.clipboard.writeText(joinLink);
      toast({
        title: "Link copied!",
        description: "Share this join link with attendees.",
      });
    } catch (error) {
      console.error('Error copying join link:', error);
      toast({
        title: "Error",
        description: "Failed to copy join link",
        variant: "destructive",
      });
    }
  };

  const switchCamera = async () => {
    if (!stream) return;
    
    // Stop current video tracks
    stream.getVideoTracks().forEach(track => track.stop());
    
    // Toggle facing mode
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);
    setIsMirrored(newFacingMode === "user");
    
    try {
      // Get new stream with opposite camera
      const qualitySettings = {
        low: { width: 320, height: 240, frameRate: 15 },
        medium: { width: 640, height: 480, frameRate: 24 },
        high: { width: 1280, height: 720, frameRate: 30 }
      };
      
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: newFacingMode,
          ...qualitySettings[videoQuality]
        },
        audio: true
      });
      
      // Set video track state based on current preference
      newStream.getVideoTracks().forEach(track => {
        track.enabled = isVideoEnabled;
      });
      
      // Keep audio always enabled for WebRTC
      newStream.getAudioTracks().forEach(track => {
        track.enabled = true;
      });
      
      setStream(newStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      
      toast({
        title: "Camera switched",
        description: `Using ${newFacingMode === "user" ? "front" : "back"} camera`,
      });
    } catch (error) {
      console.error("Error switching camera:", error);
      // Revert facing mode if switching fails
      setFacingMode(facingMode);
      toast({
        title: "Error",
        description: "Failed to switch camera",
        variant: "destructive",
      });
    }
  };

  const togglePictureInPicture = async () => {
    if (!videoRef.current || !pipSupported) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (isVideoEnabled && stream) {
        await videoRef.current.requestPictureInPicture();
        toast({
          title: "Picture-in-Picture enabled",
          description: "Your video will stay visible while you multitask",
        });
      }
    } catch (error) {
      console.error("Error toggling Picture-in-Picture:", error);
      toast({
        title: "Error",
        description: "Failed to enable Picture-in-Picture mode",
        variant: "destructive",
      });
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "default" ? "gallery" : "default");
  };

  const toggleMirror = () => {
    setIsMirrored(!isMirrored);
    toast({
      title: isMirrored ? "Mirror disabled" : "Mirror enabled",
      description: isMirrored ? "See yourself as others see you" : "See yourself as in a mirror",
    });
  };

  const handleVideoQualityChange = async (quality: 'low' | 'medium' | 'high') => {
    setVideoQuality(quality);
    localStorage.setItem('videoQuality', quality);
    
    toast({
      title: "Video quality updated",
      description: `Video quality set to ${quality}. Changes will apply on next video restart.`,
    });
  };

  const sendReaction = async (emoji: string) => {
    if (!reactionsChannel) return;
    
    await reactionsChannel.send({
      type: 'broadcast',
      event: 'reaction',
      payload: { emoji }
    });
    
    toast({
      title: `Sent ${emoji}`,
      description: "Reaction sent to all participants",
    });
  };

  const getDisplayName = () => {
    return (
      user?.user_metadata?.display_name ||
      sessionStorage.getItem('guestName') ||
      user?.email ||
      "Guest"
    );
  };

  const getSpeakerKey = (speakerId: string | null) => {
    if (!speakerId) return null;
    if (speakerId === 'local') {
      return user?.id || getGuestUserId();
    }
    return speakerId;
  };

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getSpeakingTimeForUser = (userId: string) => {
    const data = speakingDurations.get(userId);
    if (!data) return '00:00';

    const total = data.totalMs + (data.lastStarted ? Date.now() - data.lastStarted : 0);
    return formatDuration(total);
  };

  const hasExceededLimit = (userId: string) => {
    if (!speakingLimitMinutes) return false;
    const limitMs = Number(speakingLimitMinutes) * 60 * 1000;
    const data = speakingDurations.get(userId);
    const total = (data?.totalMs || 0) + (data?.lastStarted ? Date.now() - data.lastStarted : 0);
    return total >= limitMs;
  };

  const toggleWebinarMode = async () => {
    if (!meeting || !user || meeting.host_id !== user.id) {
      toast({
        title: "Host only",
        description: "Only hosts can toggle webinar mode.",
        variant: "destructive",
      });
      return;
    }

    const newMode = !isWebinarMode;

    try {
      const { error } = await supabase
        .from('meetings')
        .update({ is_webinar_mode: newMode })
        .eq('id', meeting.id);

      if (error) throw error;

      setIsWebinarMode(newMode);
      
      toast({
        title: newMode ? "Webinar mode enabled" : "Webinar mode disabled",
        description: newMode 
          ? "Attendees will join muted with camera off" 
          : "Normal meeting mode restored",
      });

      // If enabling webinar mode, mute all non-host participants
      if (newMode) {
        enforceAttendeeMute();
      }
    } catch (error) {
      console.error('Error toggling webinar mode:', error);
      toast({
        title: "Error",
        description: "Failed to toggle webinar mode",
        variant: "destructive",
      });
    }
  };

  const handleRaiseHand = async () => {
    if (!meetingId || !meeting) return;
    
    const currentUserId = user?.id || getGuestUserId();
    
    try {
      if (hasRaisedHand) {
        // Lower hand
        const { error } = await supabase
          .from('raised_hands')
          .update({ lowered_at: new Date().toISOString() })
          .eq('meeting_id', meetingId)
          .eq('user_id', currentUserId);

        if (error) throw error;
        
        setHasRaisedHand(false);
        toast({
          title: "Hand lowered",
          description: "Your hand has been lowered",
        });
      } else {
        // Raise hand
        const { error } = await supabase
          .from('raised_hands')
          .insert({
            meeting_id: meetingId,
            user_id: currentUserId,
            guest_name: user ? null : sessionStorage.getItem('guestName')
          });

        if (error) throw error;
        
        setHasRaisedHand(true);
        toast({
          title: "Hand raised",
          description: "The host has been notified",
        });
      }
    } catch (error: any) {
      console.error('Error handling raised hand:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update hand status",
        variant: "destructive",
      });
    }
  };

  const inviteToSpeak = async (userId: string) => {
    if (!meetingId || !meeting || !user || meeting.host_id !== user.id) return;

    try {
      const { error } = await supabase
        .from('raised_hands')
        .update({ invited_to_speak: true })
        .eq('meeting_id', meetingId)
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: "Invited to speak",
        description: "Participant can now unmute and turn on camera",
      });
    } catch (error) {
      console.error('Error inviting to speak:', error);
      toast({
        title: "Error",
        description: "Failed to invite participant",
        variant: "destructive",
      });
    }
  };

  const dismissRaisedHand = async (userId: string) => {
    if (!meetingId || !meeting || !user || meeting.host_id !== user.id) return;

    try {
      const { error } = await supabase
        .from('raised_hands')
        .delete()
        .eq('meeting_id', meetingId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error dismissing raised hand:', error);
    }
  };

  const allowParticipantToSpeak = async (userId: string, name?: string) => {
    if (!meetingId || !meeting || !user || meeting.host_id !== user.id) return;

    try {
      const { error } = await supabase
        .from('raised_hands')
        .upsert({
          meeting_id: meetingId,
          user_id: userId,
          guest_name: name || 'Participant',
          invited_to_speak: true,
          lowered_at: null,
          raised_at: new Date().toISOString()
        }, { onConflict: 'meeting_id,user_id' });

      if (error) throw error;

      toast({
        title: "Microphone allowed",
        description: `${name || 'Participant'} can now unmute even without raising a hand.`,
      });
    } catch (error: any) {
      console.error('Error granting speaking permission:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to allow microphone access',
        variant: "destructive",
      });
    }
  };

  const revokeSpeakingInvite = async (userId: string) => {
    if (!meetingId || !meeting || !user || meeting.host_id !== user.id) return;

    try {
      const { error } = await supabase
        .from('raised_hands')
        .update({ invited_to_speak: false, lowered_at: new Date().toISOString() })
        .eq('meeting_id', meetingId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error revoking speaking permission:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to revoke permission',
        variant: "destructive",
      });
    }
  };

  const canUserUnmute = () => {
    if (!isWebinarMode || !meeting) return true;
    if (user && meeting.host_id === user.id) return true;

    const currentUserId = user?.id || getGuestUserId();
    return raisedHands.some(hand => hand.userId === currentUserId && hand.invited);
  };

  const enforceAttendeeMute = async () => {
    if (!meetingId || (user && meeting?.host_id === user.id)) return;

    setIsAudioEnabled(false);
    setIsVideoEnabled(false);

    // Disable tracks locally
    stream?.getAudioTracks().forEach(track => (track.enabled = false));
    stream?.getVideoTracks().forEach(track => (track.enabled = false));

    const attendeeId = user?.id || getGuestUserId();

    await supabase
      .from("meeting_participants")
      .update({ is_audio_enabled: false, is_video_enabled: false })
      .eq("meeting_id", meetingId)
      .eq("user_id", attendeeId);
  };

  const fetchRaisedHands = async () => {
    if (!meetingId) return;

    try {
      const { data, error } = await supabase
        .from('raised_hands')
        .select('*')
        .eq('meeting_id', meetingId)
        .is('lowered_at', null)
        .order('raised_at', { ascending: true });

      if (error) throw error;

      const formattedHands = (data || []).map(hand => ({
        userId: hand.user_id,
        name: hand.guest_name || 'Participant',
        timestamp: new Date(hand.raised_at).getTime(),
        invited: hand.invited_to_speak
      }));

      setRaisedHands(formattedHands);

      // Check if current user has raised hand
      const currentUserId = user?.id || getGuestUserId();
      setHasRaisedHand(formattedHands.some(h => h.userId === currentUserId));
    } catch (error) {
      console.error('Error fetching raised hands:', error);
    }
  };

  useEffect(() => {
    if (isWebinarMode && meeting && user?.id !== meeting.host_id) {
      enforceAttendeeMute();
    }

    if (!isWebinarMode) {
      setHasRaisedHand(false);
    }
  }, [isWebinarMode, meeting?.host_id, meetingId, user?.id]);

  useEffect(() => {
    const updateElapsed = () => {
      setElapsedMinutes(Math.max(0, Math.floor((Date.now() - meetingStartTime) / 60000)));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 15000);
    return () => clearInterval(interval);
  }, [meetingStartTime]);

  useEffect(() => {
    const rotation = setInterval(() => {
      setIcebreakerIndex((prev) => (prev + 1) % ICEBREAKER_QUESTIONS.length);
      setAnsweredIcebreakers(new Set());
    }, 60000);

    return () => clearInterval(rotation);
  }, []);

  const currentIcebreaker = ICEBREAKER_QUESTIONS[icebreakerIndex];

  const handleIcebreakerVote = (option: string) => {
    if (!currentIcebreaker) return;
    if (answeredIcebreakers.has(currentIcebreaker.id)) return;

    setIcebreakerResponses((prev) => {
      const current = prev[currentIcebreaker.id] || {};
      const updated = {
        ...prev,
        [currentIcebreaker.id]: {
          ...current,
          [option]: (current[option] || 0) + 1,
        },
      };
      return updated;
    });

    setAnsweredIcebreakers((prev) => new Set(prev).add(currentIcebreaker.id));
  };

  const getParticipantDisplayName = (participantId: string | null) => {
    if (!participantId) return null;
    if (participantId === 'local') {
      return user?.user_metadata?.display_name || sessionStorage.getItem('guestName') || 'You';
    }

    const match = participants.find((p) => p.user_id === participantId);
    if (match) {
      return match.profiles?.display_name || match.guest_name || 'Participant';
    }

    const info = participantInfoMap.get(participantId);
    return info?.name || 'Participant';
  };

  const getElapsedLabel = () => {
    const hours = Math.floor(elapsedMinutes / 60);
    const minutes = elapsedMinutes % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m elapsed`;
    }
    return `${minutes}m elapsed`;
  };

  const galleryParticipants = useMemo(() => {
    const tiles: Array<[string, MediaStream | null]> = [];
    const remoteMap = new Map(remoteStreams);

    participants.forEach((p) => {
      if (p.user_id === currentUserId) return;
      tiles.push([p.user_id, remoteMap.get(p.user_id) ?? null]);
      remoteMap.delete(p.user_id);
    });

    remoteMap.forEach((stream, peerId) => {
      if (peerId === currentUserId) return;
      tiles.push([peerId, stream]);
    });

    return tiles;
  }, [remoteStreams, participants, currentUserId]);

  const participantTilesForGrid = useMemo<GridParticipantTile[]>(() => {
    const participantLookup = new Map(participants.map((p) => [p.user_id, p]));
    const guestDisplayName = sessionStorage.getItem('guestName') || undefined;

    const tiles: GridParticipantTile[] = [
      {
        id: 'local',
        stream: stream ?? null,
        name: user?.user_metadata?.display_name || guestDisplayName || 'You',
        isLocal: true,
        isVideoEnabled,
        isAudioEnabled,
      },
    ];

    galleryParticipants.forEach(([peerId, mediaStream]) => {
      const profile = participantLookup.get(peerId);
      const info = participantInfoMap.get(peerId);
      const displayName =
        info?.name ||
        profile?.profiles?.display_name ||
        profile?.guest_name ||
        `Participant ${peerId.slice(0, 4)}`;

      const liveVideoEnabled = Boolean(mediaStream?.getVideoTracks().some((track) => track.enabled));
      const liveAudioEnabled = Boolean(mediaStream?.getAudioTracks().some((track) => track.enabled));

      // Prefer the real-time track state when available so stale participant metadata
      // can't incorrectly mark an active camera/mic as off for remote viewers.
      const videoEnabled = liveVideoEnabled || info?.isVideoEnabled || false;
      const audioEnabled = liveAudioEnabled || (info?.isAudioEnabled ?? true);

      tiles.push({
        id: peerId,
        stream: mediaStream,
        name: displayName,
        isAudioEnabled: audioEnabled,
        isVideoEnabled: videoEnabled,
      });
    });

    return tiles;
  }, [galleryParticipants, isAudioEnabled, isVideoEnabled, participantInfoMap, participants, stream, user?.user_metadata?.display_name]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const cleanup = () => {
    participantsChannel?.unsubscribe();
    chatChannel?.unsubscribe();
    meetingChannel?.unsubscribe();
    reactionsChannel?.unsubscribe();
    webinarChannel?.unsubscribe();
    
    // Stop all local tracks
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    // Clean up remote audio elements
    remoteAudioRefs.current.forEach((audioElement) => {
      audioElement.pause();
      audioElement.srcObject = null;
    });
    remoteAudioRefs.current.clear();
  };

  // Compute derived values before early returns (Rules of Hooks)
  const activeSpeakerName = meeting ? getParticipantDisplayName(activeSpeaker) : null;
  const activeSpeakerTime = activeSpeaker ? getSpeakingTimeForUser(activeSpeaker) : "0:00";
  const timeboxProgress = plannedDurationMinutes
    ? Math.min((elapsedMinutes / plannedDurationMinutes) * 100, 150)
    : 0;
  const connectionQuality = useMemo(() => {
    if (connectionHealth.length === 0) {
      return { label: "Connecting", tone: "text-muted-foreground", dot: "bg-muted" };
    }

    const iceStates = connectionHealth.map(([, health]) => health.iceState || health.state);
    if (iceStates.some(state => state === 'failed')) {
      return { label: "Poor connection", tone: "text-destructive", dot: "bg-destructive" };
    }

    if (iceStates.some(state => state === 'disconnected' || state === 'checking')) {
      return { label: "Stabilizing", tone: "text-amber-500", dot: "bg-amber-400" };
    }

    return { label: "Good connection", tone: "text-emerald-500", dot: "bg-emerald-500" };
  }, [connectionHealth]);

  const effectiveSpotlight = viewMode === "gallery" ? null : spotlightedParticipant;

  const handleSpotlightChange = useCallback((participantId: string | null) => {
    setSpotlightedParticipant(participantId);
    if (participantId) {
      setViewMode("default");
    }
  }, [setSpotlightedParticipant, setViewMode]);

  const renderSpeakingTimeBadge = useCallback((participantId: string) => {
    const targetId = participantId === 'local' ? currentUserId : participantId;
    const exceeded = hasExceededLimit(targetId);
    const timeLabel = getSpeakingTimeForUser(targetId);

    return (
      <div className="px-2 py-1 rounded-md text-[11px] bg-background/80 flex items-center gap-1">
        <Timer className="h-3 w-3" />
        <span className={exceeded ? "text-destructive font-semibold" : ""}>{timeLabel}</span>
      </div>
    );
  }, [currentUserId, getSpeakingTimeForUser, hasExceededLimit]);

  const renderLocalActions = useCallback(
    () => (
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleMirror}
          className="rounded-full h-8 w-8"
          title={isMirrored ? "Disable mirror" : "Enable mirror"}
        >
          <FlipHorizontal className="h-4 w-4" />
        </Button>
        {isMobile && (
          <Button
            variant="secondary"
            size="icon"
            onClick={switchCamera}
            className="rounded-full h-8 w-8"
            title="Switch camera"
          >
            <SwitchCamera className="h-4 w-4" />
          </Button>
        )}
        {pipSupported && (
          <Button
            variant="secondary"
            size="icon"
            onClick={togglePictureInPicture}
            className="rounded-full h-8 w-8"
            title={isPipEnabled ? "Exit Picture-in-Picture" : "Enter Picture-in-Picture"}
          >
            <PictureInPicture className="h-4 w-4" />
          </Button>
        )}
      </div>
    ),
    [isMirrored, isMobile, isPipEnabled, pipSupported, switchCamera, toggleMirror, togglePictureInPicture]
  );

  if (!meetingId || !meetingIdIsValid || resolvingMeetingLink) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <RefreshCcw className="h-10 w-10 mx-auto text-primary animate-spin" />
          <p className="text-lg font-semibold text-foreground">Resolving your meeting link…</p>
          <p className="text-muted-foreground">
            We&apos;re verifying the meeting code from your link. You&apos;ll be redirected automatically.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Video className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading meeting...</p>
        </div>
      </div>
    );
  }

  if (showPreJoin && meeting) {
    return (
      <PreJoinScreen
        meetingTitle={meeting.title}
        onJoin={handlePreJoinComplete}
        joinDisabled={isBeforeScheduledStart}
        scheduledStartTime={scheduledStartTime}
      />
    );
  }

  // Show waiting room if enabled and user/guest needs approval
  if (showWaitingRoom && meeting) {
    return (
      <WaitingRoom
        meetingId={meeting.id}
        userId={user?.id || getGuestUserId()}
        guestName={sessionStorage.getItem('guestName') || undefined}
        onAdmitted={() => {
          setShowWaitingRoom(false);
          setIsAdmitted(true);
        }}
        onDenied={() => {
          navigate("/");
        }}
      />
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Meeting not found</p>
          <Button onClick={() => navigate("/")} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-hidden">
      <div className="sr-only" aria-hidden>
        {remoteStreams.map(([peerId]) => (
          <audio
            key={peerId}
            ref={registerRemoteAudioElement(peerId)}
            autoPlay
            playsInline
          />
        ))}
      </div>

      {joiningOverlayVisible && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-card border border-border px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 text-sm">
            <RefreshCcw className="h-4 w-4 animate-spin text-primary" />
            <div>
              <p className="font-semibold text-foreground">Joining...</p>
              <p className="text-muted-foreground">Preparing your streams</p>
            </div>
          </div>
        </div>
      )}

      {meeting.is_recording && user?.id !== meeting.host_id && (
        <RecordingConsent
          isRecording={meeting.is_recording}
          meetingTitle={meeting.title}
          hostName={hostName}
          onAccept={() => {}}
          onDecline={leaveMeeting}
        />
      )}

      {/* Header */}
      {isHost && meeting.waiting_room_enabled && (
        <div className="bg-primary/10 border border-primary/30 text-primary px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold">
            <Users className="h-4 w-4" />
            <span>Start/Admit attendees</span>
          </div>
          <p className="text-xs text-primary/80">Waiting room is enabled. Use the participants panel to admit guests.</p>
        </div>
      )}

      {/* Modern Meeting Header */}
      <header className="border-b border-border bg-gradient-to-r from-background via-muted/50 to-background px-3 md:px-6 py-3 flex items-center justify-between gap-3 md:gap-4 shrink-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="hidden sm:inline-flex" onClick={() => navigate("/")} aria-label="Back to dashboard">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Meeting room</p>
            <h1 className="text-sm md:text-lg font-semibold truncate max-w-[220px] md:max-w-[420px]">{meeting.title}</h1>
          </div>
          {isRecording && (
            <Badge variant="destructive" className="ml-1 animate-pulse text-xs gap-1">
              <Circle className="h-2 w-2 fill-current" />
              Recording
            </Badge>
          )}
        </div>

        <div className="hidden md:flex items-center gap-2 md:gap-3">
          <Badge variant="outline" className="text-xs gap-1.5">
            <Timer className="h-3 w-3" />
            {elapsedTime}
          </Badge>
          <Badge variant="outline" className="text-xs gap-1">
            <Users className="h-3 w-3" />
            {participants.length}
          </Badge>
          <Badge variant="outline" className="text-xs gap-1.5">
            <span className={`h-2 w-2 rounded-full ${connectionQuality.dot}`} />
            Connection
          </Badge>
          <div className="flex items-center gap-2 bg-card/80 border border-border rounded-full px-3 py-1 text-xs shadow-sm">
            <span className="font-medium">Code {meeting.meeting_code}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyJoinLink} aria-label="Copy join link">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy join link</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex items-center gap-1.5 md:gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={toggleViewMode}
                aria-label={viewMode === "gallery" ? "Switch to speaker view" : "Switch to gallery view"}
              >
                {viewMode === "gallery" ? <Maximize2 className="h-4 w-4" /> : <Grid3x3 className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Toggle layout</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setBackgroundSelectorOpen(true)}
                aria-label="Choose background"
              >
                <PictureInPicture className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Virtual background</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setParticipantsPanelOpen(true)}
                aria-label="View participants"
              >
                <Users className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Participants</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setChatPanelOpen(true)}
                aria-label="Open chat"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Chat</TooltipContent>
          </Tooltip>
        </div>
      </header>


      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
        {/* Main Video Area */}
        <div className="flex-1 flex flex-col overflow-hidden relative bg-background">
          {/* Video Grid */}
          <div className="flex-1 p-3 md:p-4 lg:p-6 overflow-auto">
            <ParticipantVideoGrid
              participants={participantTilesForGrid}
              activeSpeaker={activeSpeaker}
              spotlightedParticipant={effectiveSpotlight}
              onSpotlightChange={handleSpotlightChange}
              renderLocalActions={renderLocalActions}
              renderSpeakingTime={renderSpeakingTimeBadge}
              localVideoRef={videoRef}
            />
          </div>
          {/* Modern control bar */}
          <div className="shrink-0 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75 border-t border-border/70">
            <div className="flex items-center justify-center gap-2 md:gap-4 px-4 py-4 md:py-5 max-w-5xl mx-auto rounded-full bg-card/80 border border-border shadow-lg">
              {/* Mic */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isAudioEnabled ? "ghost" : "destructive"}
                    size="lg"
                    onClick={toggleAudio}
                    className="h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                  >
                    {isAudioEnabled ? <Mic className="h-5 w-5 md:h-6 md:w-6" /> : <MicOff className="h-5 w-5 md:h-6 md:w-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>{isAudioEnabled ? "Mute" : "Unmute"}</p></TooltipContent>
              </Tooltip>

              {/* Video */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isVideoEnabled ? "ghost" : "destructive"}
                    size="lg"
                    onClick={toggleVideo}
                    className="h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                  >
                    {isVideoEnabled ? <Video className="h-5 w-5 md:h-6 md:w-6" /> : <VideoOff className="h-5 w-5 md:h-6 md:w-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>{isVideoEnabled ? "Stop Video" : "Start Video"}</p></TooltipContent>
              </Tooltip>

              {/* Screen Share - Desktop */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isScreenSharing ? "secondary" : "ghost"}
                    size="lg"
                    onClick={toggleScreenShare}
                    className="hidden md:flex h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                  >
                    {isScreenSharing ? <MonitorOff className="h-5 w-5 md:h-6 md:w-6" /> : <Monitor className="h-5 w-5 md:h-6 md:w-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>{isScreenSharing ? "Stop Sharing" : "Share Screen"}</p></TooltipContent>
              </Tooltip>

              {/* View Mode - Desktop */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={viewMode === "gallery" ? "secondary" : "ghost"}
                    size="lg"
                    onClick={() => {
                      setViewMode(viewMode === "default" ? "gallery" : "default");
                      if (viewMode !== "default") {
                        setSpotlightedParticipant(null);
                      }
                    }}
                    className="hidden lg:flex h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                  >
                    {viewMode === "gallery" ? <Maximize2 className="h-5 w-5 md:h-6 md:w-6" /> : <Grid3x3 className="h-5 w-5 md:h-6 md:w-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>{viewMode === "gallery" ? "Speaker View" : "Gallery View"}</p></TooltipContent>
              </Tooltip>

              {/* Spotlight Toggle - Desktop */}
              {spotlightedParticipant && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => setSpotlightedParticipant(null)}
                      className="hidden lg:flex h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                    >
                      <PinOff className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Unpin Spotlight</p></TooltipContent>
                </Tooltip>
              )}

              {/* Settings */}
              <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="lg"
                        className="hidden md:flex h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                      >
                        <Settings className="h-5 w-5 md:h-6 md:w-6" />
                      </Button>
                    </DialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent><p>Settings</p></TooltipContent>
                </Tooltip>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Meeting Settings</DialogTitle>
                    <DialogDescription>Adjust your preferences</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm mb-2 block">Video Quality</Label>
                      <Select value={videoQuality} onValueChange={(value: any) => setVideoQuality(value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low (320p)</SelectItem>
                          <SelectItem value="medium">Medium (480p)</SelectItem>
                          <SelectItem value="high">High (720p)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => {
                          setShowSettings(false);
                          setBackgroundSelectorOpen(true);
                        }}
                      >
                        <PictureInPicture className="h-4 w-4 mr-2" />
                        Virtual Background
                      </Button>
                      {user && meeting.host_id === user.id && (
                        <>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => {
                              setShowSettings(false);
                              toggleWebinarMode();
                            }}
                          >
                            <Megaphone className="h-4 w-4 mr-2" />
                            {isWebinarMode ? "Disable Webinar Mode" : "Enable Webinar Mode"}
                          </Button>
                          {canUseRecording() ? (
                            <Button
                              variant={isRecording ? "destructive" : "outline"}
                              className="w-full justify-start"
                              onClick={() => {
                                setShowSettings(false);
                                if (isRecording) {
                                  handleStopRecording();
                                } else {
                                  handleStartRecording();
                                }
                              }}
                            >
                              <Circle className={`h-4 w-4 mr-2 ${isRecording ? 'fill-current' : ''}`} />
                              {isRecording ? 'Stop Recording' : 'Start Recording'}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full justify-start opacity-60"
                              onClick={() => {
                                setShowSettings(false);
                                navigate('/pricing');
                              }}
                            >
                              <Circle className="h-4 w-4 mr-2" />
                              Recording (Upgrade Required)
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Share Meeting */}
              <Dialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="lg"
                        className="hidden md:flex h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                        data-tour="share-export"
                      >
                        <Copy className="h-5 w-5 md:h-6 md:w-6" />
                      </Button>
                    </DialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent><p>Share</p></TooltipContent>
                </Tooltip>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Share Meeting</DialogTitle>
                    <DialogDescription>Invite others to join</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm mb-1 block">Meeting Code</Label>
                      <div className="flex gap-2">
                        <Input value={meeting.meeting_code} readOnly className="font-mono" />
                        <Button size="sm" onClick={copyMeetingCode}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Join Link</Label>
                      <div className="flex gap-2">
                        <Input value={joinLinkValue} readOnly className="font-mono" />
                        <Button size="sm" onClick={copyJoinLink}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm mb-2 block">QR Code</Label>
                      <MeetingQRCode meetingCode={meeting.meeting_code} meetingTitle={meeting.title} />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Leave */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="destructive"
                    size="lg"
                    onClick={handleLeaveClick}
                    className="h-12 w-12 md:h-14 md:w-14 rounded-full hover:scale-105 transition-all"
                  >
                    <PhoneOff className="h-5 w-5 md:h-6 md:w-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Leave</p></TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>


      {/* Floating Action Buttons for Chat & Participants */}
      <div className="fixed right-4 bottom-28 md:bottom-32 z-40 flex flex-col gap-3">
        <MobileChatDrawer
          messages={messages}
          currentMessage={currentMessage}
          onMessageChange={setCurrentMessage}
          onSendMessage={sendMessage}
          onReact={sendChatReaction}
          onSendFile={sendFileAttachment}
          uploadingFile={uploadingFile}
          isOnline={isOnline}
          messagesEndRef={messagesEndRef}
        />
        <MobileParticipantsDrawer
          participants={participants}
          currentUserId={user?.id || getGuestUserId()}
          waitingRoomContent={
            user && meeting.host_id === user.id && meeting.waiting_room_enabled ? (
              <div className="p-4">
                <WaitingRoomQueue meetingId={meeting.id} />
              </div>
            ) : undefined
          }
        />
      </div>

      {/* Desktop quick-access panels */}
      <Dialog open={participantsPanelOpen} onOpenChange={setParticipantsPanelOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Participants ({participants.length})
            </DialogTitle>
            <DialogDescription>See everyone currently in the room.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              {participants.length === 0 ? (
                <p className="text-sm text-muted-foreground">No one else is here yet.</p>
              ) : (
                participants.map((participant) => (
                  <div key={participant.user_id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 bg-muted/40">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>{(participant.guest_name || participant.user_id || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{participant.guest_name || participant.user_id}</p>
                        <p className="text-xs text-muted-foreground">{participant.role === "host" ? "Host" : "Guest"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {participant.is_audio_enabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                      {participant.is_video_enabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={chatPanelOpen} onOpenChange={setChatPanelOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" /> Chat
            </DialogTitle>
            <DialogDescription>Stay connected with everyone in the call.</DialogDescription>
          </DialogHeader>
          <MeetingChatSidebar
            messages={messages}
            currentMessage={currentMessage}
            onMessageChange={setCurrentMessage}
            onSendMessage={sendMessage}
            onSendReaction={sendChatReaction}
            onSendFile={sendFileAttachment}
            uploadingFile={uploadingFile}
            isOnline={isOnline}
            messagesEndRef={messagesEndRef}
            className="h-[70vh]"
          />
        </DialogContent>
      </Dialog>

        {/* Virtual Background Selector Dialog */}
        <Dialog open={backgroundSelectorOpen} onOpenChange={setBackgroundSelectorOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Virtual Background</DialogTitle>
              <DialogDescription>Choose a background effect</DialogDescription>
            </DialogHeader>
            <VirtualBackgroundSelector
              videoStream={displayStream}
              onBackgroundChange={setVirtualBackground}
              open={backgroundSelectorOpen}
              onOpenChange={setBackgroundSelectorOpen}
            />
          </DialogContent>
        </Dialog>

      </div>

      {/* Connection Status - Bottom Left */}
      <div className="fixed left-4 bottom-4 z-30 hidden md:block">
        <Badge variant="outline" className="gap-2">
          <span className={`h-2 w-2 rounded-full ${connectionQuality.dot}`} />
          <span className={`text-xs ${connectionQuality.tone}`}>{connectionQuality.label}</span>
        </Badge>
      </div>

      <AudioCheckPrompt
        open={showAudioCheck}
        onSkip={handleAudioCheckSkip}
        onConfirm={handleAudioCheckConfirm}
        defaultMicId={preferredMicDeviceId}
        defaultSpeakerId={preferredSpeakerDeviceId}
      />

      <AlertDialog open={Boolean(connectionError)} onOpenChange={() => clearConnectionError()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" /> Connection issue
            </AlertDialogTitle>
            <AlertDialogDescription>
              {connectionError || "We weren't able to set up audio/video. Try a quick recovery option."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
            <AlertDialogAction onClick={() => { clearConnectionError(); leaveMeeting(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto">
              Leave &amp; Rejoin
            </AlertDialogAction>
            <AlertDialogAction onClick={() => { clearConnectionError(); setAudioCheckCompleted(false); setShowAudioCheck(true); }} className="w-full sm:w-auto">
              Switch device
            </AlertDialogAction>
            <AlertDialogAction onClick={async () => { if (isAudioEnabled) { await toggleAudio(); } clearConnectionError(); }} className="w-full sm:w-auto">
              Join without audio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating Reactions */}
      <FloatingReactions reactions={reactions} />
      
      {/* Leave Meeting Confirmation Dialog */}
      <AlertDialog open={showLeaveConfirmation} onOpenChange={setShowLeaveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave this meeting? You can rejoin anytime using the meeting code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Leave Meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
